import {
    RequestMethod,
    RequestStatus,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import {
    QuotaConfig,
    QuotaInfoType,
    QuotaPartitionReduction,
    QuotaPerEntityPeriod,
} from 'dg-web-shared/model/QuotaConfig.ts';
import { Outlet, useNavigate, useOutletContext, useParams } from 'react-router';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { Box, InputLabel, Stack, TextField } from '@mui/material';
import { Delete, PieChart } from '@mui/icons-material';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { useEffect, useState } from 'react';
import { makeLocalizedText, Message } from 'dg-web-shared/lib/Localized.ts';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../ui/modal/OperatorRoutedModal.tsx';
import { OperatorRoutedModalContent } from '../layout/components/OperatorRoutedModalContent.tsx';
import { HeaderComponent } from '../layout/components/HeaderComponent.tsx';
import { HeaderCancelSaveButtons } from '../layout/components/HeaderCancelSaveButtons.tsx';
import { HeaderActionCloseButtons } from '../layout/components/HeaderActionCloseButtons.tsx';
import { MaximumQuotasReductionText } from './OperatorQuotasReduction.tsx';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { DateTime } from 'luxon';
import { ReadOnlyTextField } from '../ui/material-ui/ReadOnlyTextField.tsx';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import Alert from '@mui/material/Alert';

export function OperatorQuotasDetail() {
    const urlParams = useParams();
    const contractQuotaPartitionId = urlParams.contractQuotaPartitionId ?? null;

    const [quotaResultState, refetchQuotaDetail] = useServerFetch<
        QuotaDetailResult,
        { contractQuotaPartitionId: string | null },
        null
    >(
        () => ({
            url: `/ui-api/operator-account/quotas/${contractQuotaPartitionId}`,
        }),
        { contractQuotaPartitionId: contractQuotaPartitionId },
    );

    return (
        <OperatorRoutedModal
            backUrl={'..'}
            variant={ModalVariant.SMALL}
            render={controller => (
                <OperatorAsyncLoadedSection
                    requestState={quotaResultState}
                    render={result => (
                        <OperatorQuotasDetailModal
                            contractQuotaPartitionId={
                                result.contractQuotaPartitionId
                            }
                            quotaDetailResult={result}
                            onSuccess={() =>
                                controller.setShouldConfirmBeforeLeave(false)
                            }
                            onDirtyStateChange={
                                controller.setShouldConfirmBeforeLeave
                            }
                            refetchQuotaDetail={refetchQuotaDetail}
                        />
                    )}
                />
            )}
        />
    );
}

function OperatorQuotasDetailModal({
    contractQuotaPartitionId,
    quotaDetailResult,
    onSuccess,
    onDirtyStateChange,
    refetchQuotaDetail,
}: {
    contractQuotaPartitionId: number;
    quotaDetailResult: QuotaDetailResult;
    onSuccess: () => void;
    onDirtyStateChange: (shouldConfirm: boolean) => void;
    refetchQuotaDetail: () => void;
}) {
    const { refetchQuota } = useOperatorQuotaOutlet();
    const [updateQuotaLimitState, updateQuotaLimit] = useServerWrite<
        UpdateQuotaLimitsPayload,
        null
    >(({ contractQuotaPartitionId }) => ({
        url: `/ui-api/operator-account/quotas/${contractQuotaPartitionId}`,
        method: RequestMethod.PUT,
    }));
    const [newQuotaConfigs, setNewQuotaConfigs] = useState(
        quotaDetailResult.quotaConfigs,
    );
    const [remark, setRemark] = useState('');
    const isDirty = quotaDetailResult.quotaConfigs
        .map((config, index) => config.limit != newQuotaConfigs[index].limit)
        .some(isChanged => isChanged);
    const navigate = useNavigate();
    const { language } = useLanguage();
    const localize = makeLocalizedText(language);
    useEffect(() => {
        onDirtyStateChange(isDirty);
    }, [isDirty, onDirtyStateChange]);
    useServerSuccessEffect(updateQuotaLimitState, () => {
        onSuccess();
        refetchQuota();
        navigate('..');
    });

    const [deleteQuotaReductionState, deleteQuotaReduction] = useServerWrite<
        DeleteQuotaReductionPayload,
        null
    >(({ contractQuotaPartitionId, contractQuotaPartitionReductionId }) => ({
        url: `/ui-api/operator-account/quotas/${contractQuotaPartitionId}/reduction/${contractQuotaPartitionReductionId}`,
        method: RequestMethod.DELETE,
    }));
    useServerSuccessEffect(deleteQuotaReductionState, () => {
        refetchQuota();
        refetchQuotaDetail();
    });
    const [deleteReductionId, setDeleteReductionId] = useState<number | null>(
        null,
    );
    const hasOverlappingQuota = quotaDetailResult.quotaConfigs.some(
        config => config.type === QuotaInfoType.CONTRACT_PURCHASE_OVERLAPPING,
    );
    const quotaReductionDisabled =
        quotaDetailResult.quotaReductions.length >= 3;

    return (
        <OperatorRoutedModalContent
            header={
                <HeaderComponent
                    Icon={PieChart}
                    title={
                        <Localized
                            de="Kontingent"
                            fr="Contingent"
                            it="Contingente"
                            en="Quota"
                        />
                    }
                    editMode={isDirty}
                    buttons={
                        isDirty ? (
                            <HeaderCancelSaveButtons
                                onCancel={() => {
                                    setNewQuotaConfigs(
                                        quotaDetailResult.quotaConfigs,
                                    );
                                    setRemark('');
                                }}
                                onSave={() => {
                                    updateQuotaLimit({
                                        contractQuotaPartitionId:
                                            contractQuotaPartitionId,
                                        quotaConfigs: newQuotaConfigs,
                                        remark: remark,
                                    });
                                }}
                                saveDisabled={remark == ''}
                                loading={
                                    updateQuotaLimitState.status ===
                                        RequestStatus.PENDING ||
                                    deleteQuotaReductionState.status ===
                                        RequestStatus.PENDING
                                }
                            />
                        ) : (
                            <HeaderActionCloseButtons
                                dropdownItems={
                                    hasOverlappingQuota
                                        ? [
                                              {
                                                  key: 'reduction',
                                                  label: (
                                                      <Localized
                                                          de="Reduzierung erfassen"
                                                          fr="Enregistrer une réduction"
                                                          it="Registrare una riduzione"
                                                          en="Record reduction"
                                                      />
                                                  ),
                                                  onClickCallback: () =>
                                                      navigate('reduction'),
                                                  disabled:
                                                      quotaReductionDisabled,
                                                  tooltip:
                                                      quotaReductionDisabled ? (
                                                          <MaximumQuotasReductionText />
                                                      ) : undefined,
                                              },
                                          ]
                                        : undefined
                                }
                            />
                        )
                    }
                />
            }
            body={
                <Stack spacing={2}>
                    {(updateQuotaLimitState.status === RequestStatus.ERROR ||
                        deleteQuotaReductionState.status ===
                            RequestStatus.ERROR) && (
                        <Alert severity="error">
                            <Localized
                                de="Ein unerwarteter Fehler ist aufgetreten."
                                fr="Une erreur inattendue s'est produite."
                                it="Si è verificato un errore imprevisto."
                                en="An unexpected error has occurred."
                            />
                        </Alert>
                    )}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexGrow: 1,
                                flexDirection: 'column',
                            }}
                        >
                            <InputLabel>
                                <Localized
                                    de="Typ"
                                    fr="Type"
                                    it="Tipo"
                                    en="Type"
                                />
                            </InputLabel>
                            {newQuotaConfigs.map(quotaConfig => (
                                <Box
                                    key={JSON.stringify(quotaConfig)}
                                    sx={{
                                        color: theme => theme.palette.blue.main,
                                        fontWeight: '500',
                                        fontSize: '16px',
                                        paddingTop: '4px',
                                        paddingBottom: '5px',
                                    }}
                                >
                                    <QuotaPartitionText
                                        quotaConfig={quotaConfig}
                                    />
                                </Box>
                            ))}
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                width: '150px',
                                flexGrow: 0,
                                flexDirection: 'column',
                            }}
                        >
                            <InputLabel>
                                <Localized
                                    de="Limite"
                                    fr="Limite"
                                    it="Limite"
                                    en="Limit"
                                />
                            </InputLabel>
                            {newQuotaConfigs.map((quotaConfig, index) => (
                                <TextField
                                    key={JSON.stringify(quotaConfig)}
                                    value={quotaConfig.limit}
                                    onChange={e => {
                                        const value = e.target.value;
                                        if (/^\d*$/.test(value)) {
                                            setNewQuotaConfigs(
                                                newQuotaConfigs.map(
                                                    (config, changeIndex) =>
                                                        index == changeIndex
                                                            ? {
                                                                  ...config,
                                                                  limit: Number(
                                                                      value,
                                                                  ),
                                                              }
                                                            : config,
                                                ),
                                            );
                                        }
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                    {isDirty && (
                        <TextField
                            label={
                                <Localized
                                    de="Bemerkung"
                                    fr="Remarque"
                                    it="Osservazione"
                                    en="Remark"
                                />
                            }
                            value={remark}
                            onChange={e => setRemark(e.target.value)}
                        />
                    )}
                    <TextField
                        label={
                            <Localized
                                de="Produkttypen"
                                fr="Types de produits"
                                it="Tipi di prodotto"
                                en="Product types"
                            />
                        }
                        disabled
                        multiline
                        value={quotaDetailResult.productOptionNames
                            .map(name => localize(name))
                            .join('\n')}
                    />
                    {quotaDetailResult.quotaReductions.length !== 0 && (
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Reduktionen"
                                    fr="Réductions"
                                    it="Riduzioni"
                                    en="Reductions"
                                />
                            }
                            value={
                                <Box>
                                    {quotaDetailResult.quotaReductions.map(
                                        quotaReduction => {
                                            const validFrom =
                                                Formatter.dayMonthYear(
                                                    DateTime.fromISO(
                                                        quotaReduction.validFrom,
                                                    ),
                                                );
                                            const validTo =
                                                Formatter.dayMonthYear(
                                                    DateTime.fromISO(
                                                        quotaReduction.validTo,
                                                    ),
                                                );
                                            return (
                                                <Stack
                                                    key={
                                                        quotaReduction.createdAt
                                                    }
                                                    direction={'row'}
                                                    sx={theme => ({
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent:
                                                            'space-between',
                                                        paddingBottom:
                                                            theme.spacing(1),
                                                    })}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: '220px',
                                                            wordWrap:
                                                                'break-word',
                                                            whiteSpace:
                                                                'normal',
                                                            overflowWrap:
                                                                'break-word',
                                                        }}
                                                    >
                                                        {quotaReduction.remark ??
                                                            EmDash}
                                                    </Box>
                                                    <Box>
                                                        {`${validFrom} - ${validTo}`}
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection:
                                                                'row',
                                                            alignItems:
                                                                'center',
                                                        }}
                                                    >
                                                        {`-${quotaReduction.reductionCount}`}
                                                        <Box
                                                            onClick={() =>
                                                                setDeleteReductionId(
                                                                    quotaReduction.contractQuotaPartitionReductionId,
                                                                )
                                                            }
                                                            sx={{
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                            }}
                                                        >
                                                            <Delete />
                                                        </Box>
                                                    </Box>
                                                </Stack>
                                            );
                                        },
                                    )}
                                </Box>
                            }
                        />
                    )}
                    <DeleteQuotaReductionDialog
                        open={deleteReductionId != null}
                        onCancel={() => setDeleteReductionId(null)}
                        onDelete={() => {
                            if (deleteReductionId) {
                                deleteQuotaReduction({
                                    contractQuotaPartitionId,
                                    contractQuotaPartitionReductionId:
                                        deleteReductionId,
                                });
                            }
                        }}
                    />
                    <QuotaDetailOutlet
                        context={{
                            contractQuotaPartitionId:
                                quotaDetailResult.contractQuotaPartitionId,
                            quotaConfigs: quotaDetailResult.quotaConfigs,
                            refetchQuota: refetchQuota,
                            refetchQuotaDetail: refetchQuotaDetail,
                        }}
                    />
                </Stack>
            }
        />
    );
}

function DeleteQuotaReductionDialog({
    open,
    onCancel,
    onDelete,
}: {
    open: boolean;
    onCancel: () => void;
    onDelete: () => void;
}) {
    return (
        <ConfirmationDialog
            open={open}
            confirmLabel={
                <Localized
                    de="Löschen"
                    fr="Effacher"
                    it="Elimina"
                    en="Delete"
                />
            }
            onConfirm={onDelete}
            onCancel={onCancel}
            title={
                <Localized
                    de="Reduktion löschen"
                    fr="Supprimer réduction"
                    it="Eliminare riduzione"
                    en="Delete reduction"
                />
            }
            dialogVersion={ConfirmationDialogVersions.SOFT_WARNING}
        >
            <p>
                <Localized
                    de="Möchten Sie die ausgewählte Reduktion wirklich löschen?"
                    fr="Souhaitez-vous vraiment supprimer la réduction sélectionnée ?"
                    it="Sei sicuro di voler eliminare la riduzione selezionata?"
                    en="Do you really want to delete the selected reduction?"
                />
            </p>
        </ConfirmationDialog>
    );
}

function QuotaPartitionText({ quotaConfig }: { quotaConfig: QuotaConfig }) {
    switch (quotaConfig.type) {
        case QuotaInfoType.CONTRACT_PURCHASE_OVERLAPPING:
            return (
                <Localized
                    de="Gleichzeitig gültig"
                    fr="Valide simultanément"
                    it="Valido contemporaneamente"
                    en="Valid simultaneously"
                />
            );
        case QuotaInfoType.CONTRACT_PURCHASE_PER_ENTITY:
            return <QuotaPartitionPerEntityText period={quotaConfig.period} />;
    }
}

function QuotaPartitionPerEntityText({
    period,
}: {
    period: QuotaPerEntityPeriod;
}) {
    switch (period) {
        case QuotaPerEntityPeriod.CALENDAR_MONTH:
            return (
                <Localized
                    de="Pro Monat / Kennzeichen"
                    fr="Par mois / immatriculation"
                    it="Per mese / targa"
                    en="Per month / license plate"
                />
            );
        case QuotaPerEntityPeriod.CALENDAR_YEAR:
            return (
                <Localized
                    de="Pro Jahr / Kennzeichen"
                    fr="Par année / immatriculation"
                    it="Per anno / targa"
                    en="Per year / license plate"
                />
            );
    }
}

type QuotaDetailOutletContext = {
    contractQuotaPartitionId: number;
    quotaConfigs: QuotaConfig[];
    refetchQuota: () => void;
    refetchQuotaDetail: () => void;
};

export function useQuotaDetailOutletContext() {
    return useOutletContext<QuotaDetailOutletContext>();
}

export function QuotaDetailOutlet({
    context,
}: {
    context: QuotaDetailOutletContext;
}) {
    return <Outlet context={context} />;
}

interface QuotaDetailResult {
    contractQuotaPartitionId: number;
    productOptionNames: Message[];
    quotaConfigs: QuotaConfig[];
    quotaReductions: QuotaPartitionReduction[];
}

interface UpdateQuotaLimitsPayload {
    contractQuotaPartitionId: number;
    quotaConfigs: QuotaConfig[];
    remark: string;
}

interface DeleteQuotaReductionPayload {
    contractQuotaPartitionReductionId: number;
    contractQuotaPartitionId: number;
}

function useOperatorQuotaOutlet() {
    return useOutletContext<OperatorQuotaOutletContext>();
}

export function OperatorQuotaOutlet({
    context,
}: {
    context: OperatorQuotaOutletContext;
}) {
    return <Outlet context={context} />;
}

interface OperatorQuotaOutletContext {
    refetchQuota: () => void;
}
